import React, { useEffect, useState } from 'react';
import {
  channelMark,
  channelName,
  dayTime,
  localZone,
  postText,
  postTime,
  ReachPost,
  time,
} from './reach';

/** What the panel can do to a post. `at` is the picked time for 'schedule'. */
export type PostAction = 'approve' | 'skip' | 'retry' | 'post_now' | 'schedule' | 'discard';

const inputValue = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const TONE: Record<string, { dot: string; border: string; label: string }> = {
  posted: { dot: 'var(--ink-green)', border: 'var(--ink-line)', label: 'Posted' },
  publishing: { dot: 'var(--ink-green)', border: 'var(--ink-line)', label: 'Posting' },
  scheduled: { dot: 'var(--ink-accent)', border: 'var(--ink-line)', label: 'Scheduled' },
  waiting: { dot: 'var(--ink-amber)', border: 'var(--ink-amber)', label: 'Waiting on you' },
  // A post whose time passed without going out (Prototype v7: "Didn't post").
  missed: { dot: 'var(--ink-red)', border: 'var(--ink-red)', label: 'Didn’t post' },
  // Saved without a time: nothing goes out until it is given one.
  draft: { dot: 'var(--ink-ink3)', border: 'var(--ink-line2)', label: 'Draft' },
};

/**
 * One post, opened from the Calendar's week or from a channel's "Last post
 * didn't go out · Fix" (Prototype v7): what it says, when, and — for one that
 * didn't go out — why, with Try again / Skip. A waiting post can be approved or
 * skipped here too.
 */
export function PostPanel({
  post,
  busy,
  onClose,
  onAct,
}: {
  post: ReachPost;
  busy: boolean;
  onClose: () => void;
  onAct: (kind: PostAction, at?: Date) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [at, setAt] = useState(() => inputValue(new Date(Date.now() + 60 * 60 * 1000)));
  const atOk = new Date(at).getTime() > Date.now() + 60 * 1000;
  const tone = TONE[post.reachStatus] || TONE.scheduled;
  const t = postTime(post);
  const channel = channelName(post.platform);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const missed = post.reachStatus === 'missed';
  const waiting = post.reachStatus === 'waiting';
  const draft = post.reachStatus === 'draft';
  return (
    <>
      <div className="rc-panel-back" onClick={onClose} />
      <aside className="rc-panel" role="dialog" aria-label={`${channel} post`}>
        <div className="rc-panel-head">
          <span className="rc-mark">{channelMark(post.platform)}</span>
          <div className="rc-panel-title">
            <strong>{channel}</strong>
            <span className="rc-panel-status">
              <span className="ink-dot" style={{ background: tone.dot }} />
              <span style={{ color: missed ? 'var(--ink-red)' : undefined }}>{tone.label}</span>
              {t ? <span className="ink-muted"> · {dayTime(t)}</span> : null}
            </span>
          </div>
          <button type="button" className="rc-panel-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="rc-panel-body">
          <div className="rc-panel-post">{postText(post) || 'Untitled post'}</div>
          {missed ? (
            <p className="rc-panel-why">
              {channel} didn’t take this post{t ? ` at ${time(t)}` : ''}
              {post.failure ? `: ${post.failure}` : ''}. Nothing was posted.
            </p>
          ) : null}
        </div>
        {draft && picking ? (
          <div className="rc-panel-body" style={{ paddingTop: 0 }}>
            <input
              className="ink-site-input"
              type="datetime-local"
              value={at}
              min={inputValue(new Date())}
              onChange={(e) => setAt(e.target.value)}
              aria-invalid={!atOk}
              aria-label="When it goes out"
            />
            <span className="ink-muted" style={{ fontSize: 12 }}>
              Your time zone: {localZone()}
            </span>
          </div>
        ) : null}
        {draft ? (
          <div className="rc-panel-foot">
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              disabled={busy}
              onClick={() => onAct('discard')}
            >
              Delete draft
            </button>
            {picking ? (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={busy || !atOk}
                onClick={() => onAct('schedule', new Date(at))}
              >
                Schedule
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="ink-btn"
                  disabled={busy}
                  onClick={() => onAct('post_now')}
                >
                  Post now
                </button>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={busy}
                  onClick={() => setPicking(true)}
                >
                  Pick a time
                </button>
              </>
            )}
          </div>
        ) : null}
        {missed || waiting ? (
          <div className="rc-panel-foot">
            <button type="button" className="ink-btn" disabled={busy} onClick={() => onAct('skip')}>
              Skip
            </button>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={busy}
              onClick={() => onAct(missed ? 'retry' : 'approve')}
            >
              {missed ? 'Try again' : 'Approve'}
            </button>
          </div>
        ) : null}
      </aside>
    </>
  );
}
